import { useState, useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Plus, Edit, Trash2, Package, Loader2, AlertCircle, Layers, Image as ImageIcon, Globe } from 'lucide-react'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { RichTextEditor } from '@/components/rich-text-editor'
import { useProducts } from '@/hooks/use-products'
import { productService } from '@/services/product-service'
import { MediaPicker } from '@/components/media/media-picker'
import { getMediaUrl } from '@/utils/url-utils'
import { toast } from 'sonner'
import { RefreshCw } from 'lucide-react'

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
]

export type LocalizedString = {
  tr: string
  [key: string]: string
}

const getLocalizedText = (val: string | LocalizedString | undefined): string => {
  if (!val) return ''
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val)
      return parsed?.tr || parsed?.en || val
    } catch {
      return val
    }
  }
  return val?.tr || val?.en || ''
}

const parseLocalizedObj = (val: string | LocalizedString | undefined): LocalizedString => {
  if (!val) return { tr: '' }
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val)
      return typeof parsed === 'object' && parsed !== null ? parsed : { tr: val }
    } catch {
      return { tr: val }
    }
  }
  return val as LocalizedString
}

const SUPPORTED_LANGUAGES = [
  { code: 'tr', name: 'Türkçe' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'ru', name: 'Русский' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'ar', name: 'العربية' },
]

const localizedStringSchema = z.object({
  tr: z.string().min(2, { message: 'En az 2 karakter olmalıdır' }),
  en: z.string().optional(),
  fr: z.string().optional(),
  es: z.string().optional(),
  ru: z.string().optional(),
  nl: z.string().optional(),
  ar: z.string().optional(),
})

const categorySchema = z.object({
  name: localizedStringSchema,
  slug: localizedStringSchema,
  description: localizedStringSchema.optional(),
  parentIds: z.array(z.string()).default([]),
  isActive: z.boolean().default(true),
  imageUrl: z.string().optional(),
  seo: z.object({
    meta_title: localizedStringSchema.optional(),
    meta_description: localizedStringSchema.optional(),
    meta_keywords: localizedStringSchema.optional(),
  }).optional(),
})

type CategoryFormValues = z.infer<typeof categorySchema>

function KategorilerPage() {
  const { categories, isLoading, isError, createCategory, updateCategory, deleteCategory } = useProducts()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<any>(null)
  const [categoryToDelete, setCategoryToDelete] = useState<any>(null)
  const [activeLang, setActiveLang] = useState('tr')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false)

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: { tr: '' },
      slug: { tr: '' },
      description: { tr: '' },
      parentIds: [],
      isActive: true,
      imageUrl: '',
      seo: {
        meta_title: { tr: '' },
        meta_description: { tr: '' },
        meta_keywords: { tr: '' },
      }
    },
  })

  useEffect(() => {
    if (selectedCategory) {
      // Handle legacy parentId by converting it to parentIds array if parentIds is missing/empty
      const initialParentIds = selectedCategory.parentIds && selectedCategory.parentIds.length > 0
        ? selectedCategory.parentIds
        : (selectedCategory.parentId ? [selectedCategory.parentId] : [])

      form.reset({
        name: parseLocalizedObj(selectedCategory.name),
        slug: parseLocalizedObj(selectedCategory.slug),
        description: selectedCategory.description ? parseLocalizedObj(selectedCategory.description) : undefined,
        parentIds: initialParentIds,
        isActive: selectedCategory.isActive ?? true,
        imageUrl: selectedCategory.imageUrl || '',
        seo: {
          meta_title: selectedCategory.seo?.meta_title ? parseLocalizedObj(selectedCategory.seo.meta_title) : { tr: '' },
          meta_description: selectedCategory.seo?.meta_description ? parseLocalizedObj(selectedCategory.seo.meta_description) : { tr: '' },
          meta_keywords: selectedCategory.seo?.meta_keywords ? parseLocalizedObj(selectedCategory.seo.meta_keywords) : { tr: '' },
        }
      })
    } else {
      form.reset({
        name: { tr: '' },
        slug: { tr: '' },
        description: { tr: '' },
        parentIds: [],
        isActive: true,
        imageUrl: '',
        seo: {
          meta_title: { tr: '' },
          meta_description: { tr: '' },
          meta_keywords: { tr: '' },
        }
      })
    }
  }, [selectedCategory, form])

  // Simple slug generation
  const generateSlug = (text: string) => {
    return text
      .trim()
      .replace(/İ/g, 'i')
      .replace(/I/g, 'i')
      .replace(/ı/g, 'i')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  const handleNameChange = (val: string, lang: string) => {
    const currentName = form.getValues('name') || { tr: '' }
    form.setValue('name', { ...currentName, [lang]: val })

    if (!selectedCategory) {
      const currentSlug = form.getValues('slug') || { tr: '' }
      form.setValue('slug', { ...currentSlug, [lang]: generateSlug(val) })
    }
  }

  const handleBulkTranslate = async () => {
    if (!confirm('Tüm ürün kategorileri için eksik çeviriler AI ile tamamlanacak. Emin misiniz?')) return

    setIsTranslating(true)
    try {
      const res = await productService.bulkTranslateCategories()
      toast.success(res.message || 'Toplu çeviri başarıyla tamamlandı')
      window.location.reload()
    } catch (error) {
      console.error('Bulk translate error:', error)
      toast.error('Toplu çeviri sırasında bir hata oluştu')
    } finally {
      setIsTranslating(false)
    }
  }

  const onSubmit = async (values: CategoryFormValues) => {
    setIsSubmitting(true)
    try {
      const payload: any = {
        ...values,
        name: JSON.stringify(values.name),
        slug: JSON.stringify(values.slug),
      }
      if (values.description) {
        payload.description = JSON.stringify(values.description)
      }

      if (selectedCategory) {
        await updateCategory({ id: selectedCategory._id || selectedCategory.id, data: payload })
      } else {
        await createCategory(payload)
      }
      setIsDialogOpen(false)
      setSelectedCategory(null)
      form.reset()
    } catch (error: any) {
      console.error('Kategori kaydedilirken hata oluştu:', error)
      // Mutations already handle their own toasts
      if (!error?.response?.data?.message && !error?.response?.data?.errors) {
        import('@/utils/handle-server-error').then(({ handleServerError }) => {
          handleServerError(error)
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (categoryToDelete) {
      try {
        await deleteCategory(categoryToDelete._id || categoryToDelete.id)
        setIsDeleteDialogOpen(false)
        setCategoryToDelete(null)
      } catch (error) {
        // Error is handled by mutation toast
      }
    }
  }

  const mainCategories = categories.filter((c: any) =>
    (!c.parentIds || c.parentIds.length === 0) && !c.parentId
  )
  const subCategories = categories.filter((c: any) =>
    (c.parentIds && c.parentIds.length > 0) || c.parentId
  )

  const getParentNames = (parentIds: string[]) => {
    if (!parentIds || parentIds.length === 0) return ''
    return parentIds
      .map((id) => {
        const catName = categories.find((c: any) => (c._id || c.id) === id)?.name
        return catName ? getLocalizedText(catName) : null
      })
      .filter(Boolean)
      .join(', ')
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[60vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex flex-col items-center justify-center min-h-[400px] text-destructive">
            <AlertCircle className="h-8 w-8 mb-4" />
            <p className="font-semibold">Kategoriler yüklenirken bir hata oluştu.</p>
            <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
              Tekrar Dene
            </Button>
          </div>
        </Main>
      </>
    )
  }

  const CategoryGrid = ({ data, showParent = false }: { data: any[]; showParent?: boolean }) => (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
      {data.map((category: any) => (
        <Card key={category._id || category.id} className="hover:shadow-md transition-shadow overflow-hidden">
          {category.imageUrl && (
            <div className="w-full h-128 bg-muted relative border-b">
              <img src={getMediaUrl(category.imageUrl)} alt={getLocalizedText(category.name)} className="w-full h-full object-cover" />
            </div>
          )}
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Package className="h-4 w-4" />
              {getLocalizedText(category.name)}
            </CardTitle>
            <Badge variant={category.isActive ? 'default' : 'secondary'}>
              {category.isActive ? 'Aktif' : 'Pasif'}
            </Badge>
          </CardHeader>
          <CardContent>
            {showParent && (category.parentIds?.length > 0 || category.parentId) && (
              <div className="mb-3">
                <span className="text-[10px] uppercase font-bold text-muted-foreground bg-muted px-1.5 py-0.5 rounded block mb-1">
                  Üst Kategoriler:
                </span>
                <span className="text-[11px] font-medium text-foreground line-clamp-2">
                  {getParentNames(category.parentIds || (category.parentId ? [category.parentId] : []))}
                </span>
              </div>
            )}
            {!showParent && (
              <>
                <div className="text-2xl font-bold">{category.productCount || 0}</div>
                <p className="text-xs text-muted-foreground">ürün bulunuyor</p>
              </>
            )}
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  setSelectedCategory(category)
                  setIsDialogOpen(true)
                }}
              >
                <Edit className="mr-2 h-3 w-3" />
                Düzenle
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={() => {
                  setCategoryToDelete(category)
                  setIsDeleteDialogOpen(true)
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      {data.length === 0 && (
        <div className="col-span-full text-center py-10 bg-muted/20 rounded-lg border-2 border-dashed">
          <Package className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold">Henüz kategori bulunmuyor</h3>
          <p className="text-sm text-muted-foreground">Yeni bir kategori ekleyerek başlayın.</p>
        </div>
      )}
    </div>
  )

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Kategoriler</h1>
            <p className="text-sm text-muted-foreground">Ürün kategorilerini detaylıca yönetin</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleBulkTranslate}
              disabled={isTranslating}
              className="border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
            >
              {isTranslating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Kategorileri Çevir
            </Button>
            <Button onClick={() => {
              setSelectedCategory(null)
              setIsDialogOpen(true)
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Kategori
            </Button>
          </div>
        </div>

        <Alert className="bg-muted/50 border-border">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Bilgilendirme</AlertTitle>
          <AlertDescription>
            Yeni bir <strong>ana kategori</strong> eklenirken üst kategori seçilmemelidir.
            <strong>Alt kategori</strong> eklenirken ise mutlaka en az bir üst kategori seçilmelidir.
          </AlertDescription>
        </Alert>

        <Tabs defaultValue="main" className="space-y-6">
          <TabsList className="bg-muted/50 p-1">
            <TabsTrigger value="main" className="gap-2">
              <Package className="h-4 w-4" />
              Ana Kategoriler
              <Badge variant="secondary" className="ml-1 text-[10px] px-1 pointer-events-none">
                {mainCategories.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="sub" className="gap-2">
              <Layers className="h-4 w-4" />
              Alt Kategoriler
              <Badge variant="secondary" className="ml-1 text-[10px] px-1 pointer-events-none">
                {subCategories.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="main">
            <CategoryGrid data={mainCategories} />
          </TabsContent>

          <TabsContent value="sub">
            <CategoryGrid data={subCategories} showParent={true} />
          </TabsContent>
        </Tabs>

        {/* Kategori Ekle/Düzenle Diyaloğu */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedCategory ? 'Kategoriyi Düzenle' : 'Yeni Kategori Ekle'}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-2">
                <div className="relative w-full rounded-lg border px-4 py-3 text-sm flex items-start gap-3 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300">
                  <Globe className="h-4 w-4 mt-0.5" />
                  <div>
                    <p className="font-medium mb-1 flex items-center gap-1">✨ Yapay Zeka Çevirisi Aktif</p>
                    Türkçe içerik (ad, slug, açıklama ve SEO alanları) baz alınarak tüm diller otomatik olarak çevrilecektir. Maddelerin çokluğuna ve içeriğin uzunluğuna göre bu işlem biraz vakit alabilir, lütfen işlem tamamlanana kadar bekleyiniz.
                  </div>
                </div>

                <Tabs value={activeLang} onValueChange={setActiveLang} className="w-full mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <TabsList className="bg-muted/50 p-1 flex-wrap h-auto">
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">
                          {lang.name}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-4 border p-4 rounded-md bg-muted/10 mt-0">
                      <FormField
                        control={form.control}
                        name={`name.${lang.code}` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Kategori Adı ({lang.name})</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={`Örn: Ekskavatörler (${lang.code.toUpperCase()})`}
                                value={field.value as string || ''}
                                onChange={(e) => handleNameChange(e.target.value, lang.code)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`slug.${lang.code}` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Slug (URL yolu) ({lang.name})</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="örn: ekskavatorler"
                                {...field}
                                value={field.value as string || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`description.${lang.code}` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Açıklama ({lang.name})</FormLabel>
                            <FormControl>
                              <RichTextEditor
                                value={field.value as string || ''}
                                onChange={field.onChange}
                                placeholder="Kategori hakkında bilgi..."
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="pt-4 mt-4 border-t border-dashed">
                        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                          <SearchComponent className="w-4 h-4" /> SEO Ayarları ({lang.name})
                        </h4>
                        <div className="space-y-4">
                          <FormField
                            control={form.control}
                            name={`seo.meta_title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Meta Başlık</FormLabel>
                                <FormControl>
                                  <Input placeholder="Boş bırakılırsa kategori adı kullanılır" {...field} value={field.value as string || ''} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`seo.meta_description.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Meta Açıklama</FormLabel>
                                <FormControl>
                                  <Input placeholder="Arama sonuçlarında görünecek açıklama" {...field} value={field.value as string || ''} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`seo.meta_keywords.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Meta Anahtar Kelimeler</FormLabel>
                                <FormControl>
                                  <Input placeholder="Virgülle ayırarak yazın (örn: matkap, inşaat)" {...field} value={field.value as string || ''} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
                <FormField
                  control={form.control}
                  name="imageUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Kategori Görseli (İsteğe Bağlı)</FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-4">
                          {field.value ? (
                            <div className="relative w-24 h-24 rounded-md overflow-hidden border bg-white">
                              <img src={getMediaUrl(field.value)} alt="Kategori" className="w-full h-full object-cover" />
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute top-1 right-1 h-6 w-6 z-10"
                                onClick={() => field.onChange('')}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded-md border-2 border-dashed flex items-center justify-center text-muted-foreground bg-muted/20">
                              <ImageIcon className="h-8 w-8 opacity-50" />
                            </div>
                          )}
                          <div className="flex flex-col gap-2 flex-1">
                            <Input
                              placeholder="Görsel URL veya Yükle"
                              {...field}
                              value={field.value || ''}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => setIsMediaPickerOpen(true)}
                            >
                              <ImageIcon className="h-4 w-4 mr-2" />
                              Kütüphaneden Seç
                            </Button>
                          </div>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="parentIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Üst Kategoriler (Opsiyonel)</FormLabel>
                      <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded-md p-2">
                        {mainCategories
                          .filter(c => (c._id || c.id) !== selectedCategory?._id && (c._id || c.id) !== selectedCategory?.id)
                          .map((category) => (
                            <div key={category._id || category.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`cat-${category._id || category.id}`}
                                checked={field.value?.includes(category._id || category.id)}
                                onCheckedChange={(checked) => {
                                  const val = field.value || []
                                  if (checked) {
                                    field.onChange([...val, category._id || category.id])
                                  } else {
                                    field.onChange(val.filter((id) => id !== (category._id || category.id)))
                                  }
                                }}
                              />
                              <label
                                htmlFor={`cat-${category._id || category.id}`}
                                className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                {getLocalizedText(category.name)}
                              </label>
                            </div>
                          ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                      <div className="space-y-0.5">
                        <FormLabel>Aktif</FormLabel>
                        <p className="text-[10px] text-muted-foreground">
                          Kategori listelerde görünür olacak.
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <DialogFooter className="pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
                    İptal
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Yapay zeka çevirileri yapılıyor, lütfen bekleyin...
                      </>
                    ) : selectedCategory ? (
                      'Güncelle'
                    ) : (
                      'Kaydet'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Silme Onay Diyaloğu */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Emin misiniz?</AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{categoryToDelete ? getLocalizedText(categoryToDelete.name) : ''}</strong> kategorisini silmek istediğinize emin misiniz?
                Bu işlem geri alınamaz. Eğer bu kategoriye bağlı ürünler varsa silme işlemi yapılamaz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <MediaPicker
          open={isMediaPickerOpen}
          onOpenChange={setIsMediaPickerOpen}
          onSelect={(url) => form.setValue('imageUrl', url)}
          title="Kategori Görseli Seç"
        />
      </Main>
    </>
  )
}

export const Route = createFileRoute('/_authenticated/kategoriler/')({
  component: KategorilerPage,
})
